import React, { useState, useEffect } from "react";
import {
  Check, ArrowRight, Trash2, Truck, CreditCard, User, MapPin, Gift
} from "lucide-react";
import emailjs from "@emailjs/browser";
import { generateInvoice } from "../utils/pdfGenerator";
import { db } from "../firebase";
import { collection, addDoc, serverTimestamp, getDocs, doc, updateDoc } from "firebase/firestore";

// --- STRIPE IMPORTY ---
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";

// --- RECAPTCHA IMPORT ---
import ReCAPTCHA from "react-google-recaptcha";

const stripePromise = loadStripe("pk_test_51TXfgKQ7S9cB2du6yZJaN8xLmekxhWXdUJXdcdbe79on5q6ls2FolRNjMN38iZIrOhBWRIKZnlYrlD3Z1Bym6drS00LGDARao2");

// --- KOMPONENTA PRO SAMOTNÝ PLATEBNÍ FORMULÁŘ ---
const StripePaymentForm = ({ finalTotal, onSuccess, orderId, emailParams }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: "if_required",
    });

    if (error) {
      setMessage(error.message);
      setIsProcessing(false);
    } else if (paymentIntent && paymentIntent.status === "succeeded") {
      try {
        const orderRef = doc(db, "orders", orderId);
        await updateDoc(orderRef, { status: "Zaplaceno" });

        if (emailParams) {
          const updatedParams = {
            ...emailParams,
            platebni_instrukce: "✅ Vaše platba kartou byla úspěšně přijata! Děkujeme a začínáme objednávku vyřizovat."
          };
          await emailjs.send("service_0vwmdhx", "template_m3pveai", updatedParams, "PkiNAdzSKNyLGGnqD");
        }
      } catch (err) {
        console.error("Chyba při aktualizaci po platbě:", err);
      }

      onSuccess(); 
    }
  };

  return (
    <form onSubmit={handleSubmit} className="bg-white p-6 md:p-8 rounded-xl border border-slate-200 shadow-sm animate-in fade-in zoom-in duration-500">
      <h3 className="text-xl font-bold mb-6 text-[#2a3b4c] text-center border-b pb-4">
        Zadejte platební údaje
      </h3>
      <PaymentElement />
      <button 
        disabled={isProcessing || !stripe || !elements} 
        className="mt-8 w-full bg-green-600 hover:bg-green-700 text-white font-extrabold py-4 rounded-xl shadow-lg transition transform hover:scale-[1.01] disabled:bg-slate-400 disabled:cursor-not-allowed"
      >
        {isProcessing ? "Zpracovávám platbu..." : `Zaplatit ${finalTotal.toLocaleString()} Kč`}
      </button>
      {message && <div className="text-red-500 mt-4 text-center font-bold">{message}</div>}
    </form>
  );
};

// --- HLAVNÍ KOMPONENTA KOŠÍKU ---
const CheckoutPage = ({ cart, removeFromCart, updateQuantity, navigateTo, clearCart }) => {
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [orderNumber, setOrderNumber] = useState(null); 
  const [completedOrderTotal, setCompletedOrderTotal] = useState(0); 
  
  const [clientSecret, setClientSecret] = useState("");
  const [createdOrderId, setCreatedOrderId] = useState(null);
  const [emailDataToResend, setEmailDataToResend] = useState(null);

  const [shipping, setShipping] = useState("dpd");
  const [payment, setPayment] = useState("card");
  const [packetaPoint, setPacketaPoint] = useState(null);
  const [wantsInvoice, setWantsInvoice] = useState(false);
  
  // Nový stav pro ochranu reCAPTCHA
  const [captchaVerified, setCaptchaVerified] = useState(false);

  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://widget.packeta.com/v6/www/js/library.js";
    script.async = true;
    document.head.appendChild(script);
    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  const openPacketaWidget = () => {
    if (window.Packeta && window.Packeta.Widget) {
      window.Packeta.Widget.pick(
        "12345678",
        (point) => {
          if (point) setPacketaPoint({ id: point.id, name: point.name });
        },
        { country: "cz", language: "cs" }
      );
    } else {
      alert("Mapa Zásilkovny se ještě načítá, zkuste to prosím za vteřinku.");
    }
  };

  const cartTotal = cart.reduce((sum, item) => sum + item.price * (item.quantity || 1), 0);
  const FREE_SHIPPING_THRESHOLD = 5000;
  const isFreeShipping = cartTotal >= FREE_SHIPPING_THRESHOLD;
  const remainingForFreeShipping = FREE_SHIPPING_THRESHOLD - cartTotal;
  const progressPercentage = Math.min((cartTotal / FREE_SHIPPING_THRESHOLD) * 100, 100);
  const hasCompleteSet = cart.some((item) => item.type !== "single");

  useEffect(() => {
    if (hasCompleteSet && shipping === "packeta_point") setShipping("dpd");
  }, [hasCompleteSet, shipping]);

  const shippingOptions = [
    { id: "dpd", name: "DPD Kurýr", price: isFreeShipping ? 0 : 149 },
    { id: "packeta_address", name: "Zásilkovna (Doručení na adresu)", price: isFreeShipping ? 0 : 109 },
    { id: "packeta_point", name: "Zásilkovna (Výdejní místo / Z-BOX)", price: isFreeShipping ? 0 : 79 },
    { id: "pickup", name: "Osobní odběr (Praha)", price: 0 },
  ];

  const paymentOptions = [
    { id: "card", name: "Online platba (Karta / Apple Pay / Google Pay)", price: 0 },
    { id: "transfer", name: "Bankovní převod předem", price: 0 },
    { id: "cod", name: "Dobírka (Platba při převzetí)", price: 49 },
  ];

  const selectedShipping = shippingOptions.find((s) => s.id === shipping) || shippingOptions[0];
  const selectedPayment = paymentOptions.find((p) => p.id === payment) || paymentOptions[0];
  const finalTotal = cartTotal + selectedShipping.price + selectedPayment.price;

  const handleOrderSubmit = async (e) => {
    e.preventDefault();
    
    // Dvojitá kontrola, kdyby se někdo pokusil formulář odeslat obchvatem
    if (!captchaVerified) {
      alert("Prosím, potvrďte nejprve, že nejste robot.");
      return;
    }
    
    setIsSubmitting(true);
    emailjs.init("PkiNAdzSKNyLGGnqD");

    const formData = new FormData(e.target);
    const customerName = formData.get("fullName");
    const customerEmail = formData.get("email");
    const customerPhone = formData.get("phone");
    const customerAddress = formData.get("address");
    const customerCity = formData.get("city");
    const customerZip = formData.get("zip");
    const orderNote = formData.get("orderNote") || "";

    const orderSummaryText = cart.map((cartItem) => {
      const qty = cartItem.quantity || 1;
      if (cartItem.type === "single") {
        return `📦 ${cartItem.product.name}\n   Počet: ${qty} ks — Cena: ${(cartItem.price * qty).toLocaleString()} Kč\n`;
      } else {
        return `🚲 KOMPLETNÍ SADA NA MÍRU (${qty} ks)
   • Motor: ${cartItem.items.motor?.name || "-"}
   • Baterie: ${cartItem.items.battery?.name || "-"}
   • Displej: ${cartItem.items.display?.name || "-"}
   • Brzdy: ${cartItem.items.brakes?.name || "-"}
   • Montáž: ${cartItem.items.assembly?.name || "-"}
   Cena za tyto sady: ${(cartItem.price * qty).toLocaleString()} Kč\n`;
      }
    }).join("\n" + "—".repeat(30) + "\n\n");

    const shippingMethodFull = selectedShipping.name + (packetaPoint ? ` (${packetaPoint.name})` : "");

    let dbStatus = "Nová";
    if (payment === "card") dbStatus = "Čeká na online platbu";
    else if (payment === "cod") dbStatus = "Dobírka (čeká na převzetí)";
    else if (payment === "transfer") dbStatus = "Čeká na platbu převodem";

    try {
      const ordersSnapshot = await getDocs(collection(db, "orders"));
      const nextOrderNumber = ordersSnapshot.size + 1;
      
      setOrderNumber(nextOrderNumber); 
      setCompletedOrderTotal(finalTotal);

      const orderRef = await addDoc(collection(db, "orders"), {
        orderNumber: nextOrderNumber,
        customerData: {
          name: customerName,
          email: customerEmail,
          phone: customerPhone,
          address: customerAddress,
          city: customerCity,
          zip: customerZip
        },
        orderNote: orderNote,
        cartItems: cart,
        shipping: shippingMethodFull,
        payment: selectedPayment.name,
        totalPrice: finalTotal,
        status: dbStatus,
        wantsInvoice: wantsInvoice,
        createdAt: serverTimestamp()
      });

      setCreatedOrderId(orderRef.id);

      let instrukce = "";
      if (payment === "cod") {
        instrukce = "Zboží vám zašleme na dobírku. Částku zaplatíte kurýrovi při převzetí balíčku.";
      } else if (payment === "card") {
        instrukce = "Nyní prosím dokončete platbu online ve vyskakovacím okénku. Pokud se vám platební okno z nějakého důvodu zavřelo nebo platba neprošla, kontaktujte nás pro náhradní odkaz k platbě.";
      } else {
        instrukce = `Pro zaplacení objednávky bankovním převodem prosím použijte tyto údaje:\nČíslo účtu: 123-8064070207/0100\nČástka: ${finalTotal.toLocaleString()} Kč\nVariabilní symbol: ${nextOrderNumber}`;
      }

      const templateParams = {
        order_number: nextOrderNumber.toString(),
        customer_name: customerName,
        customer_email: customerEmail,
        order_summary: orderSummaryText,
        shipping_method: shippingMethodFull,
        payment_method: selectedPayment.name, 
        total_price: finalTotal.toLocaleString() + " Kč",
        order_note: orderNote ? `Vaše poznámka: ${orderNote}` : "",
        platebni_instrukce: instrukce,
      };

      setEmailDataToResend(templateParams);
      await emailjs.send("service_0vwmdhx", "template_m3pveai", templateParams, "PkiNAdzSKNyLGGnqD");

      if (wantsInvoice) {
        generateInvoice(formData, cart, finalTotal, selectedShipping.name, selectedPayment.name, nextOrderNumber);
      }
      
      if (typeof clearCart === "function") clearCart();

      if (payment === "card") {
        try {
          const response = await fetch("https://us-central1-elektrokit-12fb0.cloudfunctions.net/createPaymentIntent", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              orderId: orderRef.id,
              finalTotal: finalTotal,
            }),
          });

          const data = await response.json();
          
          if (data.clientSecret) {
            setClientSecret(data.clientSecret); 
            setIsSubmitting(false);
          } else {
            alert("Nepodařilo se vygenerovat platební okno.");
            setIsSubmitting(false);
          }
        } catch (err) {
          console.error("Chyba při komunikaci se Stripe:", err);
          alert("Omlouváme se, spojení s platební branou selhalo.");
          setIsSubmitting(false);
        }
      } else {
        setSubmitted(true);
        setIsSubmitting(false);
      }

    } catch (error) {
      console.error("Chyba při dokončení objednávky:", error);
      alert("Omlouváme se, objednávku se nepodařilo dokončit. Zkuste to prosím znovu.");
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-20 text-center animate-in fade-in zoom-in duration-500">
        <div className="w-24 h-24 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
          <Check className="w-12 h-12" />
        </div>
        <h1 className="text-3xl font-bold text-slate-800 mb-4">Objednávka přijata!</h1>
        <p className="text-slate-600 mb-6">Děkujeme za váš nákup. Na e-mail jsme vám právě zaslali potvrzení.</p>

        {payment === "transfer" && (
          <div className="bg-amber-50 border border-amber-200 p-6 rounded-xl text-left max-w-sm mx-auto mb-8 shadow-sm">
            <h3 className="font-bold text-amber-900 text-lg mb-4 border-b border-amber-200 pb-2">Údaje pro platbu převodem</h3>
            <div className="space-y-2 text-amber-900 text-sm">
              <div className="flex justify-between items-center"><span className="font-medium text-amber-700">Číslo účtu:</span><span className="font-bold text-base">123-8064070207/0100</span></div>
              <div className="flex justify-between items-center"><span className="font-medium text-amber-700">Částka k úhradě:</span><span className="font-bold text-base">{completedOrderTotal.toLocaleString()} Kč</span></div>
              <div className="flex justify-between items-center"><span className="font-medium text-amber-700">Variabilní symbol:</span><span className="font-bold text-base">{orderNumber}</span></div>
            </div>
            <p className="text-xs text-amber-700 mt-4 pt-3 border-t border-amber-200">Vaši objednávku začneme zpracovávat hned po připsání platby.</p>
          </div>
        )}

        <div className="mt-4">
          <button onClick={() => navigateTo("home")} className="px-8 py-3 bg-slate-800 hover:bg-slate-700 transition text-white rounded-lg font-bold">Zpět na hlavní stránku</button>
        </div>
      </div>
    );
  }

  if (clientSecret) {
    return (
      <div className="max-w-xl mx-auto px-4 py-12 animate-in fade-in duration-300">
        <h2 className="text-3xl font-bold text-[#2a3b4c] text-center mb-4">Dokončení platby</h2>
        <p className="text-center text-slate-500 mb-8">Objednávka č. {orderNumber} byla vytvořena. Nyní ji prosím uhraďte.</p>
        
        <Elements stripe={stripePromise} options={{ clientSecret }}>
          <StripePaymentForm 
            finalTotal={completedOrderTotal} 
            orderId={createdOrderId}
            emailParams={emailDataToResend}
            onSuccess={() => {
              setClientSecret(""); 
              setSubmitted(true);
            }} 
          />
        </Elements>
      </div>
    );
  }

  if (cart.length === 0)
    return (
      <div className="p-20 text-center text-slate-500 text-lg">
        Váš košík je momentálně prázdný.<br />
        <button onClick={() => navigateTo("products")} className="text-green-600 font-bold mt-4 hover:underline">Přejít do E-shopu</button>
      </div>
    );

  if (step === 1) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 animate-in fade-in duration-300">
        <h1 className="text-3xl md:text-4xl font-bold text-[#2a3b4c] mb-8">Nákupní košík</h1>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-8">
          {isFreeShipping ? (
            <div className="flex items-center gap-3 text-green-600 font-bold text-lg">
              <Gift className="w-6 h-6 flex-shrink-0" /> Gratulujeme, máte od nás dopravu ZDARMA!
            </div>
          ) : (
            <div>
              <div className="flex justify-between text-sm font-bold text-slate-600 mb-3">
                <span className="flex items-center gap-2 text-amber-600"><Truck className="w-4 h-4"/> Doprava zdarma nad 5 000 Kč</span>
                <span>Chybí {remainingForFreeShipping.toLocaleString()} Kč</span>
              </div>
              <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden border border-slate-200">
                <div className="bg-amber-400 h-3 rounded-full transition-all duration-1000 ease-out relative" style={{ width: `${progressPercentage}%` }}>
                  <div className="absolute top-0 right-0 bottom-0 left-0 bg-white/20 animate-pulse"></div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-8">
          <div className="p-6 md:p-8 space-y-6">
            {cart.map((cartItem) => {
              const qty = cartItem.quantity || 1;
              return (
                <div key={cartItem.cartId} className="border-b border-slate-100 pb-6 last:border-0 last:pb-0 flex flex-col md:flex-row gap-4 md:items-center justify-between">
                  <div className="flex-1">
                    {cartItem.type === "single" ? (
                      <div className="font-bold text-[#2a3b4c] text-lg">{cartItem.product.name}</div>
                    ) : (
                      <div>
                        <div className="font-bold text-[#2a3b4c] text-lg mb-2">Kompletní sada na míru</div>
                        <ul className="text-sm text-slate-500 space-y-1 border-l-2 border-green-200 pl-3">
                          {cartItem.items.motor && <li>Motor: {cartItem.items.motor.name}</li>}
                          {cartItem.items.battery && cartItem.items.battery.id !== "bat_none" && <li>Baterie: {cartItem.items.battery.name}</li>}
                          {cartItem.items.display && <li>Displej: {cartItem.items.display.name}</li>}
                          {cartItem.items.brakes && <li>Brzdy: {cartItem.items.brakes.name}</li>}
                          {cartItem.items.assembly && <li>Montáž: {cartItem.items.assembly.name}</li>}
                        </ul>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-6 md:w-[350px] justify-between">
                    <div className="flex items-center border border-slate-200 rounded-lg bg-slate-50 shadow-sm">
                      <button onClick={() => updateQuantity(cartItem.cartId, -1)} disabled={qty <= 1} className="w-10 h-10 flex items-center justify-center text-slate-500 hover:text-green-600 disabled:opacity-30 font-bold">-</button>
                      <span className="w-10 text-center font-bold text-slate-700">{qty}</span>
                      <button onClick={() => updateQuantity(cartItem.cartId, 1)} className="w-10 h-10 flex items-center justify-center text-slate-500 hover:text-green-600 font-bold">+</button>
                    </div>
                    <div className="text-right flex-1 font-extrabold text-green-600 text-lg">{(cartItem.price * qty).toLocaleString()} Kč</div>
                    <button onClick={() => removeFromCart(cartItem.cartId)} className="text-red-400 hover:text-red-600 p-2"><Trash2 className="w-5 h-5" /></button>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="bg-slate-50 p-6 md:p-8 border-t border-slate-100 flex flex-col sm:flex-row justify-between items-center gap-6">
            <div><span className="text-slate-500 font-medium block mb-1">Cena zboží celkem:</span><span className="text-3xl font-black text-[#2a3b4c]">{cartTotal.toLocaleString()} Kč</span></div>
            <button onClick={() => setStep(2)} className="w-full sm:w-auto px-8 py-4 bg-green-600 hover:bg-green-700 text-white font-extrabold rounded-xl shadow-md transition-all flex items-center gap-2">Pokračovat k objednávce <ArrowRight className="w-5 h-5" /></button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-12 flex flex-col lg:flex-row gap-8 animate-in fade-in duration-300">
      <div className="lg:w-2/3">
        <button onClick={() => setStep(1)} className="mb-6 flex items-center text-sm font-bold text-slate-500 hover:text-green-600 transition w-fit"><ArrowRight className="w-4 h-4 mr-2 rotate-180" /> Zpět do košíku</button>
        <h1 className="text-3xl font-bold text-[#2a3b4c] mb-8">Dokončení objednávky</h1>
        
        <form onSubmit={handleOrderSubmit} className="space-y-6">
          <div className="bg-white p-6 md:p-8 rounded-xl border border-slate-200 shadow-sm">
            <h2 className="text-xl font-bold mb-6 flex items-center gap-2 text-[#2a3b4c]"><User className="w-5 h-5 text-green-600" /> Osobní údaje</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Jméno a příjmení</label><input name="fullName" required type="text" className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none" placeholder="Jan Novák"/></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Telefon</label><input name="phone" required type="tel" className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none" placeholder="+420 123 456 789"/></div>
              <div className="md:col-span-2"><label className="block text-sm font-medium text-slate-700 mb-1">E-mail</label><input name="email" required type="email" className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none" placeholder="jan.novak@email.cz"/></div>
            </div>
            <div className="mt-8 pt-6 border-t border-slate-100">
              <h3 className="font-bold text-lg mb-4 text-[#2a3b4c]">{shipping === "dpd" || shipping === "packeta_address" ? "Fakturační a doručovací adresa" : "Fakturační adresa"}</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="md:col-span-2"><label className="block text-sm font-medium text-slate-700 mb-1">Ulice a číslo popisné</label><input name="address" required type="text" className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none" placeholder="Nová 123/45"/></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">Město</label><input name="city" required type="text" className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none" placeholder="Praha"/></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1">PSČ</label><input name="zip" required type="text" className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none" placeholder="110 00"/></div>
                <div className="md:col-span-2"><label className="block text-sm font-medium text-slate-700 mb-1">Poznámka (nepovinné)</label><textarea name="orderNote" rows="3" className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 outline-none" placeholder="Cokoliv nám chcete vzkázat..."></textarea></div>
              </div>
            </div>
          </div>

          <div className="bg-white p-6 md:p-8 rounded-xl border border-slate-200 shadow-sm">
            <h2 className="text-xl font-bold mb-6 flex items-center gap-2 text-[#2a3b4c]"><Truck className="w-5 h-5 text-green-600" /> Způsob doručení</h2>
            {isFreeShipping && <div className="mb-6 p-4 bg-green-50 border border-green-200 text-green-700 rounded-lg flex items-center gap-3 font-bold"><Gift className="w-5 h-5" /> Máte nárok na dopravu ZDARMA!</div>}
            <div className="space-y-3">
              {shippingOptions.map((option) => {
                const isDisabled = hasCompleteSet && option.id === "packeta_point";
                return (
                  <div key={option.id}>
                    <label className={`flex items-center justify-between p-4 rounded-lg border transition-all ${isDisabled ? "opacity-60 bg-slate-50 border-slate-200 cursor-not-allowed" : shipping === option.id ? "border-green-500 bg-green-50/50 ring-1 ring-green-500 cursor-pointer" : "border-slate-200 hover:border-slate-300 bg-white cursor-pointer"}`}>
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-3">
                          <input type="radio" name="shipping" value={option.id} checked={shipping === option.id && !isDisabled} onChange={() => !isDisabled && setShipping(option.id)} disabled={isDisabled} className={`w-5 h-5 text-green-600 focus:ring-green-500 ${isDisabled ? "cursor-not-allowed" : "cursor-pointer"}`}/>
                          <span className="font-bold text-slate-800">{option.name}</span>
                        </div>
                        {isDisabled && <span className="text-xs text-red-500 font-bold ml-8 mt-1">Nelze využít: Sada je příliš velká a nevejde se do Z-BOXu</span>}
                      </div>
                      <span className="font-bold text-slate-700">{option.price === 0 ? "Zdarma" : `${option.price} Kč`}</span>
                    </label>
                    {shipping === "packeta_point" && option.id === "packeta_point" && !isDisabled && (
                      <div className="ml-8 mt-3 p-4 bg-slate-50 border border-slate-200 rounded-lg animate-in fade-in duration-300">
                        <button type="button" onClick={openPacketaWidget} className="w-full py-3 px-4 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg flex items-center justify-center gap-2 transition"><MapPin className="w-5 h-5" /> Vybrat pobočku / Z-BOX</button>
                        {packetaPoint && <div className="mt-4 p-3 bg-green-100 text-green-800 rounded-md font-medium flex items-center gap-2 border border-green-200"><Check className="w-5 h-5 text-green-600" /> Vybráno: {packetaPoint.name}</div>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-white p-6 md:p-8 rounded-xl border border-slate-200 shadow-sm">
            <h2 className="text-xl font-bold mb-6 flex items-center gap-2 text-[#2a3b4c]"><CreditCard className="w-5 h-5 text-green-600" /> Způsob platby</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {paymentOptions.map((option) => {
                if (shipping === "pickup" && option.id === "cod") return null;
                return (
                  <label key={option.id} className={`flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-all ${payment === option.id ? "border-green-500 bg-green-50/50 ring-1 ring-green-500" : "border-slate-200 hover:border-slate-300 bg-white"}`}>
                    <div className="flex items-center gap-3">
                      <input type="radio" name="payment" value={option.id} checked={payment === option.id} onChange={() => setPayment(option.id)} className="w-5 h-5 text-green-600 focus:ring-green-500"/>
                      <span className="font-bold text-slate-800">{option.name}</span>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="bg-slate-50 p-6 rounded-xl border border-slate-200 shadow-sm">
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={wantsInvoice} onChange={(e) => setWantsInvoice(e.target.checked)} className="w-5 h-5 mt-0.5 text-green-600 rounded focus:ring-green-500 border-slate-300 cursor-pointer"/>
              <div>
                <span className="font-bold text-slate-800 block">Vystavit a stáhnout fakturu (PDF)</span>
                <span className="text-sm text-slate-500 mt-1 block leading-relaxed">Faktura se po odeslání objednávky stáhne do zařízení. Pokud ji nepotřebujete ihned, můžete si o ni napsat kdykoliv později.</span>
              </div>
            </label>
          </div>

          {/* Ochrana reCAPTCHA */}
          <div className="flex justify-center my-6">
            <ReCAPTCHA
              sitekey="6LeorbItAAAAAJVk0MCC8dCkRQ-C3M1JMt1MANFK"
              onChange={(value) => setCaptchaVerified(!!value)}
            />
          </div>

          {/* Upravené tlačítko s kontrolou captchaVerified */}
          <button 
            type="submit" 
            disabled={isSubmitting || (shipping === "packeta_point" && !packetaPoint) || !captchaVerified} 
            className={`w-full py-4 text-white font-extrabold text-lg rounded-xl shadow-lg transition transform hover:scale-[1.01] ${isSubmitting || (shipping === "packeta_point" && !packetaPoint) || !captchaVerified ? "bg-slate-400 cursor-not-allowed" : "bg-green-600 hover:bg-green-700"}`}
          >
            {isSubmitting ? "Zpracovávám..." : 
             (shipping === "packeta_point" && !packetaPoint) ? "Nejprve vyberte pobočku Zásilkovny" : 
             !captchaVerified ? "Prosím, potvrďte že nejste robot 👆" : 
             "Závazně objednat"}
          </button>
        </form>
      </div>

      <div className="lg:w-1/3">
        <div className="bg-slate-50 p-6 md:p-8 rounded-xl border border-slate-200 sticky top-24">
          <h2 className="text-xl font-bold mb-6 border-b pb-4 text-[#2a3b4c]">Shrnutí objednávky</h2>
          <div className="mb-6 space-y-4">
            {cart.map((cartItem) => {
              const qty = cartItem.quantity || 1;
              return (
                <div key={cartItem.cartId} className="flex justify-between items-start text-sm">
                  <span className="text-slate-600 font-medium pr-4 leading-relaxed">{qty}x {cartItem.type === "single" ? cartItem.product.name : "Kompletní sada na míru"}</span>
                  <span className="font-bold text-slate-800 whitespace-nowrap">{(cartItem.price * qty).toLocaleString()} Kč</span>
                </div>
              );
            })}
          </div>
          <div className="border-t border-slate-200 pt-6 pb-6 space-y-3 text-sm">
            <div className="flex justify-between items-center"><span className="text-slate-500">Mezisoučet položek</span><span className="font-bold text-slate-800">{cartTotal.toLocaleString()} Kč</span></div>
            <div className="flex justify-between items-center"><span className="text-slate-500">Doprava</span><span className="font-bold text-slate-800">{selectedShipping.price === 0 ? "Zdarma" : `${selectedShipping.price} Kč`}</span></div>
            <div className="flex justify-between items-center"><span className="text-slate-500">Platba</span><span className="font-bold text-slate-800">{selectedPayment.price === 0 ? "Zdarma" : `${selectedPayment.price} Kč`}</span></div>
          </div>
          <div className="border-t border-slate-200 pt-6 flex justify-between items-end">
            <div><span className="text-lg font-bold text-[#2a3b4c] block">Celkem k úhradě</span><span className="text-xs text-slate-400">včetně DPH</span></div>
            <span className="text-3xl font-black text-green-600">{finalTotal.toLocaleString()} Kč</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;